import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import type { CreateLeadBody } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ApiRequestError } from "../api/http";
import { assignmentSettingsQuery } from "../api/queries";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { NameFields } from "../design/components/NameFields";
import { Reason } from "../design/components/Reason";
import { Select } from "../design/components/Select";
import { TextField } from "../design/components/TextField";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { useT, type Translate } from "../i18n/locale";
import type { TabContext } from "../session/tab-context";
import { archivedPhoneOf } from "./archive/archive-queries";
import { CityPicker, EMPTY_CITY, type CityValue } from "./card/CityPicker";
import { composePhone, EMAIL_SHAPE, PHONE_PREFIXES } from "./lead-display";
import { leadOwnerOptionsQuery, useCreateLead } from "./lead-queries";
import "./lead-list.css";

// «Nuovo Lead» (piano M2 Task 17, mock-up `lead.html`, «Nuovo Lead»): Nome e Cognome in due caselle, Nome del salone,
// Cellulare con il prefisso separato, Telefono fisso, Email, Città dal selettore dei comuni e, soltanto per chi ha la
// funzione `change_owner`, l'Owner. Il telefono è il solo dato obbligatorio (decisione del 28 settembre 2026). Il
// server riconosce i doppioni: con un numero già usato non crea nulla e offre «Apri il Lead»; con un'email già usata
// chiede conferma («Crea lo stesso»). Il numero di un Lead archiviato (piano M3 Task 12, D9) lo dice e, per chi ha
// `trash_restore`, porta agli «Archiviati» con quel numero già cercato. Dopo la creazione si apre la scheda del Lead
// nuovo (proposta del 25 settembre, n. 2). La finestra nasce a ogni apertura: i campi non ricordano la volta prima.
// Con una regola di assegnazione scelta (a turno, o tutti a una persona; piano M6, Task 13, questione 366) la scelta vuota
// dell'owner non lascia il Lead senza owner: lo dà la regola, e la voce lo dice («Con la regola di assegnazione»).

/** Il cellulare come lo riceve il server (`composePhone`): resta esportato con il suo nome per i test di «Nuovo Lead». */
export const composeMobile = composePhone;

/** Il valore di «Senza owner» nel selettore (un identificativo non è mai vuoto). */
const NO_OWNER = "";

type Field = "mobile" | "landline" | "email" | "city" | "owner" | "form";
type Problem = { field: Field; text: (t: Translate) => string };
/**
 * Un Lead che c'è già: per il numero (non si crea; `archivedPhone` è il numero se quel Lead è archiviato) o per l'email
 * (si può creare lo stesso).
 */
type Duplicate =
  | { kind: "phone"; leadId: string; canOpen: boolean; archivedPhone: string | null }
  | { kind: "email"; leadId: string; canOpen: boolean; email: string };

type Props = {
  tab: TabContext;
  /** La funzione `change_owner`: soltanto allora si sceglie l'owner; altrimenti il Lead nuovo è di chi lo crea. */
  canChangeOwner: boolean;
  /** La funzione `trash_restore`: il numero di un Lead archiviato porta agli «Archiviati». */
  canOpenArchived: boolean;
  onClose: () => void;
};

function detailString(error: ApiRequestError, key: string): string | null {
  const value = error.details?.[key];
  return typeof value === "string" ? value : null;
}

/** I campi di cui il server dice che non vanno (`422 validation_failed`, `details.fields`). */
function invalidFields(error: ApiRequestError): string[] {
  const fields = error.details?.fields;
  return Array.isArray(fields) ? fields.filter((field): field is string => typeof field === "string") : [];
}

export function NewLeadDialog({ tab, canChangeOwner, canOpenArchived, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const formId = useId();
  const create = useCreateLead(tab);
  const owners = useQuery({ ...leadOwnerOptionsQuery(tab), enabled: canChangeOwner });
  // Soltanto chi sceglie l'owner ha bisogno di sapere che cosa fa la scelta vuota.
  const rule = useQuery({ ...assignmentSettingsQuery(tab), enabled: canChangeOwner });
  const assignsByRule = rule.data !== undefined && rule.data.settings.mode !== "manual";
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [salonName, setSalonName] = useState("");
  const [prefix, setPrefix] = useState<string>(PHONE_PREFIXES[0].value);
  const [mobile, setMobile] = useState("");
  const [landline, setLandline] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState<CityValue>(EMPTY_CITY);
  const [owner, setOwner] = useState(NO_OWNER);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [duplicate, setDuplicate] = useState<Duplicate | null>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const mobileRef = useRef<HTMLInputElement>(null);
  const landlineRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const ownerRef = useRef<HTMLSelectElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  // Si comincia dal nome, come nel mock-up (la finestra altrimenti dà il focus a «Chiudi»).
  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  function fail(field: Field, text: (tr: Translate) => string) {
    setProblem({ field, text });
    const target = { mobile: mobileRef, landline: landlineRef, email: emailRef, city: cityRef, owner: ownerRef, form: submitRef }[field];
    target.current?.focus();
  }

  /** Il rifiuto del server, spiegato sul campo che lo riguarda. */
  function explain(error: unknown, address: string) {
    if (!(error instanceof ApiRequestError)) {
      fail("form", (tr) => messageForError(tr, error));
      return;
    }
    const field = detailString(error, "field") === "landline" ? "landline" : "mobile";
    switch (error.code) {
      case "phone_required":
        fail("mobile", (tr) => tr("newLead.phoneRequired"));
        return;
      case "invalid_phone": {
        const suggestion = detailString(error, "suggestion");
        if (suggestion === "landline") fail(field, (tr) => tr("newLead.looksLandline"));
        else if (suggestion === "mobile") fail(field, (tr) => tr("newLead.looksMobile"));
        else fail(field, (tr) => tr("newLead.phoneInvalid"));
        return;
      }
      case "phone_repeated_on_lead":
        fail("landline", (tr) => tr("newLead.phoneRepeated"));
        return;
      case "phone_in_use":
      case "possible_duplicate_email": {
        const leadId = detailString(error, "leadId");
        const canOpen = error.details?.canOpen === true && leadId !== null;
        if (leadId === null) break;
        setProblem(null);
        setDuplicate(
          error.code === "phone_in_use"
            ? { kind: "phone", leadId, canOpen, archivedPhone: canOpenArchived ? archivedPhoneOf(error) : null }
            : { kind: "email", leadId, canOpen, email: address },
        );
        return;
      }
      case "invalid_field_value":
        if (detailString(error, "field") === "cityIstatCode") {
          fail("city", (tr) => tr("newLead.cityInvalid"));
          return;
        }
        break;
      case "owner_not_eligible":
        fail("owner", (tr) => messageForError(tr, error));
        return;
      case "validation_failed": {
        const fields = invalidFields(error);
        if (fields.includes("email")) {
          fail("email", (tr) => tr("newLead.emailInvalid"));
          return;
        }
        if (fields.includes("mobile") || fields.includes("landline")) {
          fail(fields.includes("mobile") ? "mobile" : "landline", (tr) => tr("newLead.phoneInvalid"));
          return;
        }
        break;
      }
    }
    fail("form", (tr) => messageForError(tr, error));
  }

  async function submit(acknowledgeEmail: boolean) {
    if (create.isPending) return;
    const mobileValue = composeMobile(prefix, mobile);
    const landlineValue = landline.trim();
    const address = email.trim();
    if (!mobileValue && !landlineValue) {
      fail("mobile", (tr) => tr("newLead.phoneRequired"));
      return;
    }
    if (address && !EMAIL_SHAPE.test(address)) {
      fail("email", (tr) => tr("newLead.emailInvalid"));
      return;
    }
    if (city.text.trim() && !city.match) {
      fail("city", (tr) => tr("newLead.cityInvalid"));
      return;
    }
    setProblem(null);
    setDuplicate(null);
    const body: CreateLeadBody = {
      ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
      ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
      ...(salonName.trim() ? { salonName: salonName.trim() } : {}),
      ...(mobileValue ? { mobile: mobileValue } : {}),
      ...(landlineValue ? { landline: landlineValue } : {}),
      ...(address ? { email: address } : {}),
      ...(city.match ? { cityIstatCode: city.match.istatCode } : {}),
      ...(canChangeOwner ? { ownerMembershipId: owner === NO_OWNER ? null : owner } : {}),
      ...(acknowledgeEmail ? { acknowledgePossibleDuplicate: true } : {}),
    };
    try {
      const created = await create.mutateAsync(body);
      onClose();
      toast.show(t("newLead.created"), "success");
      void navigate({ to: "/lead/$leadId", params: { leadId: created.lead.id } });
    } catch (error) {
      explain(error, address);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // «Crea Lead» dopo l'avviso sull'email vale come conferma soltanto se l'email non è cambiata.
    const confirmed = duplicate?.kind === "email" && duplicate.email === email.trim();
    void submit(confirmed);
  }

  // L'avviso sull'email vale per l'email che il server ha visto: cambiata, l'avviso sparisce con il suo «Crea lo stesso»,
  // e «Crea Lead» la fa controllare di nuovo (riportata a quella, l'avviso torna).
  const shownDuplicate = duplicate?.kind === "email" && duplicate.email !== email.trim() ? null : duplicate;
  const errorOf = (field: Field) => (problem?.field === field ? problem.text(t) : undefined);
  const formError = errorOf("form");
  const ownerOptions = [{ value: NO_OWNER, label: t(assignsByRule ? "newLead.ownerRule" : "newLead.ownerNone") }, ...(owners.data ?? []).map((option) => ({ value: option.membershipId, label: option.displayName }))];

  return (
    <Dialog
      open
      title={t("newLead.title")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button ref={submitRef} variant="primary" type="submit" form={formId} disabled={create.isPending} aria-busy={create.isPending ? "true" : undefined}>
            {t("newLead.submit")}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack" onSubmit={handleSubmit} aria-busy={create.isPending} noValidate>
        <NameFields
          id="new-lead-name"
          firstName={{ label: t("common.firstName"), value: firstName, onChange: setFirstName, ref: firstNameRef }}
          lastName={{ label: t("common.lastName"), value: lastName, onChange: setLastName }}
        />
        <TextField id="new-lead-salon" label={t("newLead.salonName")} value={salonName} maxLength={200} autoComplete="off" onChange={(event) => setSalonName(event.target.value)} />
        <TextField
          ref={mobileRef}
          id="new-lead-mobile"
          label={t("newLead.mobile")}
          leading={
            <Select
              id="new-lead-prefix"
              label={t("newLead.prefix")}
              hideLabel
              value={prefix}
              onChange={setPrefix}
              options={PHONE_PREFIXES.map((option) => ({ value: option.value, label: t(option.label) }))}
            />
          }
          type="tel"
          inputMode="tel"
          autoComplete="off"
          maxLength={40}
          value={mobile}
          placeholder={t("newLead.mobilePlaceholder")}
          hint={t("newLead.phoneHint")}
          error={errorOf("mobile")}
          onChange={(event) => setMobile(event.target.value)}
        />
        <TextField
          id="new-lead-landline"
          ref={landlineRef}
          label={t("newLead.landline")}
          type="tel"
          inputMode="tel"
          autoComplete="off"
          maxLength={40}
          value={landline}
          error={errorOf("landline")}
          onChange={(event) => setLandline(event.target.value)}
        />
        <TextField
          id="new-lead-email"
          ref={emailRef}
          label={t("newLead.email")}
          type="email"
          autoComplete="off"
          maxLength={254}
          value={email}
          error={errorOf("email")}
          onChange={(event) => setEmail(event.target.value)}
        />
        {shownDuplicate ? <DuplicateNotice duplicate={shownDuplicate} onConfirm={() => void submit(true)} pending={create.isPending} onLeave={onClose} /> : null}
        <CityPicker id="new-lead-city" ref={cityRef} tab={tab} value={city} onChange={setCity} error={errorOf("city")} />
        {canChangeOwner ? (
          <Select id="new-lead-owner" ref={ownerRef} label={t("newLead.owner")} value={owner} onChange={setOwner} options={ownerOptions} error={errorOf("owner")} />
        ) : (
          <p className="g-field__hint">{t("newLead.ownerSelf")}</p>
        )}
        <p className="g-field__hint">{t("newLead.afterCreate")}</p>
        {formError ? (
          <p className="g-field__error" role="alert">
            {formError}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

type NoticeProps = {
  duplicate: Duplicate;
  onConfirm: () => void;
  pending: boolean;
  /** Si va agli «Archiviati»: la finestra si chiude. */
  onLeave: () => void;
};

/**
 * Il Lead che c'è già: «Apri il Lead» quando chi crea lo può aprire; per l'email anche «Crea lo stesso»; per il numero
 * di un Lead archiviato «Apri gli Archiviati», con quel numero cercato fra gli archiviati (`cercaArchiviati`): la
 * ricerca e le righe dell'elenco dietro restano quelle di prima.
 */
function DuplicateNotice({ duplicate, onConfirm, pending, onLeave }: NoticeProps) {
  const t = useT();
  const archivedPhone = duplicate.kind === "phone" ? duplicate.archivedPhone : null;
  let text: string;
  if (duplicate.kind === "email") text = t("newLead.duplicateEmail");
  else if (archivedPhone !== null) text = t("leadArchive.phoneOfArchived");
  else text = duplicate.canOpen ? t("newLead.phoneInUse") : t("newLead.phoneInUseHidden");
  return (
    <div className="g-new-lead__duplicate" role="alert">
      <Reason tone="warning">{text}</Reason>
      <div className="g-new-lead__duplicate-actions">
        {duplicate.canOpen ? (
          <Link to="/lead/$leadId" params={{ leadId: duplicate.leadId }} className="g-button" data-variant="secondary">
            {t("newLead.openLead")}
          </Link>
        ) : null}
        {archivedPhone !== null ? (
          <Link
            from="/lead"
            to="/lead"
            search={(previous) => ({ ...previous, archiviati: 1, cercaArchiviati: archivedPhone })}
            replace
            className="g-button"
            data-variant="secondary"
            onClick={onLeave}
          >
            {t("leadArchive.openArchived")}
          </Link>
        ) : null}
        {duplicate.kind === "email" ? (
          <Button variant="primary" onClick={onConfirm} disabled={pending}>
            {t("newLead.createAnyway")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
